// The network on the map: the line and station sources, which lines are
// shown and which one is highlighted, and which stations are shown (those
// on a shown line), for MapLibre's layers and the renderer alike.

import type { ExpressionSpecification, GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl';
import * as Core from '@tol/core/core';
import { STOP_LAYER, dimLines, filterLines, linesGeoJSONAsync, regionFilter } from '@tol/core/layers';
import type { LineId, LineLayout, Network, PageLine, StationId } from '@tol/core/types';
import { viewBox } from '@tol/core/onscreen';
import type { Box } from '@tol/core/onscreen';
import { preparedLayouts } from './network-layouts.ts';
import { networkStations } from './network-stations.ts';
import type { ShellState } from './state.ts';

// World retention supplies complete prefixed region snapshots. City pages
// without this metadata keep their individual-id eligibility filters.
export const networkRegions = new WeakMap<Network, readonly string[]>();

// The expression subset used by our station layers. MapLibre's getFilter
// also returns legacy filters, which cannot be composed as expressions.
function stationExpression(value: unknown): value is ExpressionSpecification {
  if (!Array.isArray(value)) return false;
  if (value[0] === 'get') return value.length === 2 && typeof value[1] === 'string';
  if (value[0] === '==') return value.length === 3 && stationExpression(value[1]) && typeof value[2] === 'boolean';
  if (value[0] === '!') return value.length === 2 && stationExpression(value[1]);
  return value[0] === 'all' && value.slice(1).every((part: unknown) => stationExpression(part));
}

export interface NetworkViewOptions {
  readonly map: MapLibreMap;
  readonly lines: readonly PageLine[];
  readonly line: Readonly<Partial<Record<LineId, PageLine>>>;
  // The network, its layouts, the shown lines and stations, the focus.
  readonly state: ShellState;
  // The set of shown stations changed (a city's own layer follows it).
  readonly onStationsVisible: ((visible: ReadonlySet<StationId>) => void) | undefined;
  // A current viewport's detail reached setData, not necessarily usable tiles.
  readonly onLinesSubmitted?: (net: Network, bounds: Box) => void;
  // Draw the trains again (a new focus dims the others).
  readonly redraw: () => void;
}

export interface NetworkView {
  // Makes net the active network. An optional retained superset keeps
  // station sources and layouts for revisits; detail submissions are active-only.
  readonly setNetwork: (net: Network, rendered?: Network) => void;
  readonly setLinesVisible: (ids: readonly LineId[], visible: boolean) => void;
  readonly setFocus: (id: LineId | null) => void;
  // Lines the network lacks, or has no track for.
  readonly missing: ReadonlySet<LineId>;
}

export function createNetworkView(options: NetworkViewOptions): NetworkView {
  const { map, lines: LINES, line: LINE, state } = options;
  const missingLines = new Set<LineId>();
  let renderedNetwork: Network | null = null;
  let renderedLayouts = new Map<LineId, LineLayout>();
  const presentStations = networkStations(LINE);
  const stationFilters = new Map<string, ReturnType<MapLibreMap['getFilter']>>();
  const appliedStationFilters = new Map<string, string>();
  let lineFilterKey: string | null = null;
  let sourceBounds: Box | null = null;
  let sourceLayouts: ReadonlyMap<LineId, LineLayout> | null = null;
  let sourceZoom = -Infinity;
  let lineLoad: AbortController | null = null;
  let cameraGeneration = 0;
  function cancelLineLoad(): void {
    lineLoad?.abort();
    lineLoad = null;
  }
  map.on('movestart', () => {
    cameraGeneration++;
    cancelLineLoad();
  });
  function drawNearbyLines(): void {
    // Returning to the already drawn view must cancel another view's pending
    // preparation even when the cache below needs no new source submission.
    cancelLineLoad();
    if (!state.net || map.isMoving()) return;
    const b = map.getBounds();
    const view: Box = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    const guard = viewBox(view, 0.2);
    const previous = sourceBounds;
    // Keep a generous pan buffer, but release country-sized geometry after
    // zooming into a city. Small camera changes send nothing to the worker.
    if (
      sourceLayouts === state.layouts &&
      map.getZoom() <= sourceZoom &&
      previous &&
      guard[0] >= previous[0] &&
      guard[1] >= previous[1] &&
      guard[2] <= previous[2] &&
      guard[3] <= previous[3] &&
      previous[2] - previous[0] <= 4 * (view[2] - view[0])
    )
      return;
    const bounds = viewBox(view, 0.75);
    const layouts = state.layouts;
    const net = state.net;
    const generation = cameraGeneration;
    const zoom = Math.ceil(map.getZoom()) + 1;
    // Subpixel detail at native density, including a further zoom level of
    // headroom for a pinch. National views need far fewer curve points.
    const latitude = Math.min(85, Math.max(Math.abs(bounds[1]), Math.abs(bounds[3])));
    const metersPerPixel = (40_075_017 * Math.cos((latitude * Math.PI) / 180)) / (512 * 2 ** zoom);
    const tolerance = (metersPerPixel * 0.25) / (window.devicePixelRatio || 1);
    const request = new AbortController();
    lineLoad = request;
    const prepare = async (): Promise<void> => {
      const data = await linesGeoJSONAsync(LINES, layouts, bounds, tolerance, request.signal);
      if (request.signal.aborted || generation !== cameraGeneration || lineLoad !== request) return;
      const source = map.getSource<GeoJSONSource>('lines');
      if (!source) return;
      sourceBounds = bounds;
      sourceLayouts = layouts;
      sourceZoom = zoom;
      // Cancellation covers our preparation only. MapLibre owns processing
      // after setData; aborting a later gesture cannot retract that update.
      lineLoad = null;
      const update = source.setData(data);
      options.onLinesSubmitted?.(net, bounds);
      await update;
    };
    void prepare().catch((error: unknown) => {
      if (!request.signal.aborted) console.warn('Track preparation failed', error);
    });
  }
  map.on('moveend', drawNearbyLines);
  function updateLineVisibility(): void {
    const regions = state.net ? networkRegions.get(state.net) : undefined;
    const hidden = new Set(
      [
        ...state.hidden,
        ...(regions === undefined
          ? Object.keys((renderedNetwork ?? state.net)?.lines ?? {}).filter((id) => !state.net?.lines[id])
          : []),
      ].toSorted(),
    );
    const key = JSON.stringify([regions, [...hidden]]);
    if (key === lineFilterKey) return;
    filterLines(map, hidden, regions);
    lineFilterKey = key;
  }
  function setNetwork(net: Network, rendered: Network = net): void {
    if (rendered !== renderedNetwork) {
      renderedNetwork = rendered;
      renderedLayouts = new Map(
        LINES.map((l) => [l.id, preparedLayouts.get(rendered)?.get(l.id) ?? Core.linePaths(rendered, l.id, true)]),
      );
    }
    const layouts = new Map<LineId, LineLayout>();
    for (const l of LINES) {
      const layout = renderedLayouts.get(l.id);
      if (net.lines[l.id] && layout) layouts.set(l.id, layout);
      if (!net.lines[l.id] || (layout?.paths.length ?? 0) === 0) missingLines.add(l.id);
      else missingLines.delete(l.id);
    }
    state.net = net;
    // Retained layouts stay available, but new source data includes only
    // active lines. Keep the cache identity when their layouts are unchanged.
    if (
      layouts.size !== state.layouts.size ||
      [...layouts].some(([id, layout]: readonly [LineId, LineLayout]) => state.layouts.get(id) !== layout)
    )
      state.layouts = layouts;
    drawNearbyLines();
    updateLineVisibility();
    updateStationVisibility(net);
  }

  // Which stations are on a shown major line. The dots, names, clicks and
  // tooltips (MapLibre's station layers) and the stopped-train rings (the
  // renderer) all follow this one set. A stop only shown minor lines serve
  // (a bus stop, minor.ts) is a stop instead: a small dot zoomed in, which
  // opens its card too.
  function updateStationVisibility(net: Network): void {
    const { visible, labels, stations, stops } = presentStations(net, renderedNetwork ?? net, state.hidden);
    state.visibleStations = visible;
    if (stations) void map.getSource<GeoJSONSource>('stations')?.setData(stations);
    if (stops) void map.getSource<GeoJSONSource>(STOP_LAYER)?.setData(stops);
    // Keep each label layer's own tier filter as well as the active-region
    // filter. Filtered dots cannot be queried by station clicks or tooltips.
    const regions = networkRegions.get(net);
    const active: ExpressionSpecification =
      regions === undefined
        ? ['in', ['get', 'id'], ['literal', [...net.stations.keys()].toSorted()]]
        : regionFilter('id', regions);
    const labeled = new Set(labels);
    const excluded = [...net.stations.keys()].filter((id) => !labeled.has(id)).toSorted();
    const names: ExpressionSpecification =
      excluded.length === 0 ? active : ['all', active, ['!', ['in', ['get', 'id'], ['literal', excluded]]]];
    for (const layer of map.getStyle().layers ?? []) {
      if (!('source' in layer) || (layer.source !== 'stations' && layer.source !== STOP_LAYER)) continue;
      if (!stationFilters.has(layer.id)) stationFilters.set(layer.id, map.getFilter(layer.id));
      const base = stationFilters.get(layer.id);
      if (base !== undefined && !stationExpression(base)) throw new TypeError('Unexpected station filter');
      const shown = layer.type === 'symbol' ? names : active;
      const filter: ExpressionSpecification = base === undefined ? shown : ['all', base, shown];
      const key = JSON.stringify(filter);
      if (key === appliedStationFilters.get(layer.id)) continue;
      map.setFilter(layer.id, filter);
      appliedStationFilters.set(layer.id, key);
    }
    options.onStationsVisible?.(visible);
  }

  function setLinesVisible(ids: readonly LineId[], visible: boolean): void {
    for (const id of ids) {
      if (visible) state.hidden.delete(id);
      else state.hidden.add(id);
    }
    if (state.focus !== null && state.hidden.has(state.focus)) setFocus(null);
    updateLineVisibility();
    if (state.net) updateStationVisibility(state.net);
  }

  function setFocus(id: LineId | null): void {
    state.focus = id;
    dimLines(map, id);
    options.redraw();
  }

  return { setNetwork, setLinesVisible, setFocus, missing: missingLines };
}
